import { HostListener, Injectable, signal } from "@angular/core";
import { SideNavStatus } from "@app/shared/models/sidenav.model";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class SidenavService {
  // Monitor changes of side nav state.
  sideNavChangeState: Subject<SideNavStatus> = new Subject<SideNavStatus>();
  printEvent = signal<boolean>(false);

  // Prevent Print Dialog from firing the breakpoint observer and causing the sidenav to misbehave.
  @HostListener("window:afterprint", ["$event"]) afterPrint(e) {
    if (e.type === "afterprint" && !e?.detail) {
      this.printEvent.set(true);
    }
  }

  private _leftNav: SideNavStatus;
  //
  // - Left Sidenav
  //
  set leftNav(sidenav: SideNavStatus) {
    this._leftNav = sidenav;
  }

  get leftNav(): SideNavStatus {
    return this._leftNav;
  }

  get isUserStateOpened(): boolean {
    return this._leftNav.isUserStateOpened;
  }

  toggleLeftNav(): void {
    if (!this._leftNav) {
      return;
    }

    this._toggleNav(this._leftNav);
  }

  requestToggleLeftNav(open: boolean, isMobile: boolean): void {
    this._requestToggleNav(this._leftNav, open, isMobile);
  }

  isLeftNavOpen(isMobile: boolean): boolean {
    return this._isNavOpen(this._leftNav, isMobile);
  }

  manualToggleLeftNav(open: boolean, isMobile: boolean) {
    if (!this._leftNav) {
      return;
    }
    this._manualToggleNav(this._leftNav, open, isMobile);
  }

  /**
   * Destroy current data
   */
  destroyData() {
    this.leftNav = null;
  }

  /**
   * Toggle left and right navs.
   * @param open
   * @param isMobile
   */
  toggleAllNavs(open: boolean, isMobile: boolean) {
    this.requestToggleLeftNav(open, isMobile);
  }

  /**
   * Emit nav object that is changed.
   * @param navObj
   */
  sideNavChanged(navObj: SideNavStatus) {
    this.sideNavChangeState.next(navObj);
  }

  /**
   * Monitor sidenav when it's opened to auto open or close.
   * @param open
   * @param isMobile
   */
  monitorSideNavOpenStatus(open: boolean, isMobile: boolean) {
    if (this.leftNav) {
      if (this.isUserStateOpened || this?.leftNav?.isExpanded) {
        this.requestToggleLeftNav(open, isMobile);
      }
    }
  }

  /**
   * User requested toggle via a button.
   * @param navObj
   */
  private _toggleNav(navObj: SideNavStatus): void {
    if (!navObj) {
      return;
    }

    navObj.isExpanded = !navObj.isExpanded;
    navObj.isUserStateOpened = !navObj.isUserStateOpened;
    this.sideNavChanged(navObj);
  }

  /**
   * Programatic request of toggle.
   * @param navObj
   * @param open
   * @param isMobile
   */
  private _requestToggleNav(navObj: SideNavStatus, open: boolean, isMobile: boolean): void {
    if (!navObj) {
      return;
    }

    let __open: boolean = open;
    navObj.sideNav.mode = "side";
    if (!isMobile) {
      navObj.keepClosed = navObj.isExpanded && !open;

      if (open && !navObj.isUserStateOpened) {
        __open = false;
      }
    } else {
      if (__open) {
        navObj.sideNav.mode = "over";
      }
    }
    navObj.isExpanded = __open;
    this.sideNavChanged(navObj);
  }

  /**
   * Trigger the Toggle for the nav based on isMobile
   * @param open
   * @param isMobile
   */
  private _manualToggleNav(navObj: SideNavStatus, open: boolean, isMobile: boolean): void {
    if (!navObj) {
      return;
    }

    if (isMobile) {
      this._requestToggleNav(navObj, open, isMobile);
    } else {
      this._toggleNav(navObj);
    }
    this.sideNavChanged(navObj);
  }

  /**
   * Determine if the nav is open
   * @param navObj
   * @param isMobile
   * @returns
   */
  private _isNavOpen(navObj: SideNavStatus, isMobile: boolean): boolean {
    return !isMobile && navObj?.isUserStateOpened && !navObj?.keepClosed;
  }
}
